import { AxiosMode } from '@/axiosModel';
import type { ApiResponse } from '@/axiosModel';

export interface AipicturebookPriceItem {
  vip_type: number;
  name: string;
  original_price: number;
  price: number;
}

export interface AipicturebookPriceActivity {
  activity: string;
  active: boolean;
  start: string;
  end: string;
  discount: number;
  label: string;
  prices: AipicturebookPriceItem[];
}

export interface PayOrderListParams {
  object_type?: number | string;
  type?: number;
  status?: number;
  invoiced?: number;
  system_type?: string;
  page?: number;
  pageSize?: number;
}

export interface PayOrderItem {
  orderNo: string;
  productName: string;
  price: string;
  createdAt: string;
  paidAmount: string;
  paidAt: string;
  status: string;
  invoiced: 0 | 1;
}

export interface PayOrderListResult {
  list: PayOrderItem[];
  total: number;
  page: number;
  pageSize: number;
}

interface BackendPayOrderListResult {
  list?: BackendPayOrderRecord[];
  lists?: BackendPayOrderRecord[];
  rows?: BackendPayOrderRecord[];
  records?: BackendPayOrderRecord[];
  data?: BackendPayOrderRecord[];
  total?: number;
  listCount?: number;
  allCount?: number;
  page?: number;
  pageSize?: number;
  pagesize?: number;
}

type BackendPayOrderPayload = BackendPayOrderListResult | BackendPayOrderRecord[];

interface BackendPayOrderGoods extends Record<string, unknown> {
  object_type?: number;
  number?: number;
  old_vip_type?: number;
  new_vip_type?: number;
}

interface BackendPayOrderRecord extends Record<string, unknown> {
  goods?: BackendPayOrderGoods[];
}

const apis = new AxiosMode();
const PAY_API_BASE = `${AxiosMode.API_HOST}/api/new_pay`;
const DEFAULT_ORDER_PAGE = 0;
const DEFAULT_ORDER_PAGE_SIZE = 20;
const DEFAULT_SYSTEM_TYPE = '0';

export async function fetchPayOrderList(
  params: PayOrderListParams = {}
): Promise<PayOrderListResult> {
  const page = params.page ?? DEFAULT_ORDER_PAGE;
  const pageSize = params.pageSize ?? DEFAULT_ORDER_PAGE_SIZE;
  const { req } = apis.post({
    postData: removeEmptyFields({
      object_type: params.object_type,
      type: params.type,
      status: params.status,
      invoiced: params.invoiced,
      system_type: params.system_type ?? DEFAULT_SYSTEM_TYPE,
      page,
      pageSize: page === 0 ? undefined : pageSize
    }),
    remotePath: `${PAY_API_BASE}/get-list`
  });
  const res = (await req) as { data?: BackendPayOrderPayload };
  const data = res.data ?? {};
  const records = getOrderRecords(data);

  return {
    list: records.map(mapBackendPayOrder),
    total: getOrderTotal(data, records.length),
    page: getNumberField(data, 'page', page),
    pageSize: getNumberField(data, 'pageSize', getNumberField(data, 'pagesize', pageSize))
  };
}

export async function fetchAipicturebookPrice(): Promise<AipicturebookPriceActivity> {
  const { req } = apis.get({ remotePath: `${PAY_API_BASE}/get-aipicturebook-price` });
  const res = (await req) as { data: ApiResponse<AipicturebookPriceActivity> };

  if (res.data.code !== 'OK') {
    throw new Error(res.data.msg || '获取绘本会员价格失败');
  }

  return res.data.data;
}

function mapBackendPayOrder(data: BackendPayOrderRecord): PayOrderItem {
  const goodsProductName = formatGoodsProductName(data.goods?.[0]);
  const price = formatMoney(
    pickValue(data, [
      'price',
      'amount',
      'orderAmount',
      'order_amount',
      'totalAmount',
      'total_amount',
      'totalMoney',
      'total_money',
      'money',
      'total_fee'
    ])
  );
  const paidAmount = formatMoney(
    pickValue(data, [
      'paidAmount',
      'paid_amount',
      'payAmount',
      'pay_amount',
      'realAmount',
      'real_amount',
      'paidPrice',
      'paid_price',
      'paymentAmount',
      'payment_amount'
    ])
  );

  return {
    orderNo: toStringValue(pickValue(data, ['id'])),
    productName:
      goodsProductName ||
      toStringValue(
        pickValue(data, [
          'productName',
          'product_name',
          'goodsName',
          'goods_name',
          'name',
          'title',
          'subject',
          'orderName',
          'order_name',
          'objectName',
          'object_name',
          'description'
        ])
      ) || '未知商品',
    price: price || '0.00',
    createdAt: formatApiTime(
      pickValue(data, [
        'createdAt',
        'created_at',
        'createTime',
        'create_time',
        'newTime',
        'new_time',
        'addTime',
        'add_time',
        'orderTime',
        'order_time'
      ])
    ),
    paidAmount: paidAmount || price || '0.00',
    paidAt: formatApiTime(
      pickValue(data, [
        'paidAt',
        'paid_at',
        'payTime',
        'pay_time',
        'paymentTime',
        'payment_time',
        'paidTime',
        'paid_time'
      ])
    ),
    status: formatOrderStatus(
      pickValue(data, ['status', 'payStatus', 'pay_status', 'orderStatus', 'order_status', 'state'])
    ),
    invoiced: Number(pickValue(data, ['invoiced'])) === 1 ? 1 : 0
  };
}

function formatGoodsProductName(goods: BackendPayOrderGoods | undefined) {
  if (!goods) return '';

  const objectType = Number(goods.object_type);

  if (objectType === 2) {
    const number = Number(goods.number);
    return Number.isFinite(number) ? `购买${number}个云币` : '购买云币';
  }

  if (objectType === 9) {
    const newVipType = Number(goods.new_vip_type);
    if (newVipType === 2) return '云展绘本_升级到高级版';
    if (newVipType === 1) return '云展绘本_升级到基础版';
  }

  return '';
}

function getOrderRecords(data: BackendPayOrderPayload): BackendPayOrderRecord[] {
  if (Array.isArray(data)) return data;

  const records = data.list ?? data.lists ?? data.rows ?? data.records ?? data.data;
  return Array.isArray(records) ? records : [];
}

function getOrderTotal(data: BackendPayOrderPayload, fallback: number) {
  if (Array.isArray(data)) return fallback;
  return Number(data.total ?? data.listCount ?? data.allCount ?? fallback) || fallback;
}

function getNumberField(
  data: BackendPayOrderPayload,
  field: keyof BackendPayOrderListResult,
  fallback: number
) {
  if (Array.isArray(data)) return fallback;
  return Number(data[field] ?? fallback) || fallback;
}

function pickValue(data: BackendPayOrderRecord, fields: string[]) {
  for (const field of fields) {
    const value = data[field];
    if (value !== undefined && value !== null && value !== '') return value;
  }

  return undefined;
}

function formatMoney(value: unknown) {
  if (value === undefined || value === null || value === '') return '';

  const numberValue = Number(value);
  if (Number.isFinite(numberValue)) return numberValue.toFixed(2);

  return toStringValue(value);
}

function formatApiTime(value: unknown) {
  if (value === undefined || value === null || value === '') return '';

  if (typeof value === 'string' && Number.isNaN(Number(value))) return value;

  const timestamp = Number(value);
  if (!Number.isFinite(timestamp) || timestamp <= 0) return '';

  const normalizedTimestamp = timestamp < 1_000_000_000_000 ? timestamp * 1000 : timestamp;
  const date = new Date(normalizedTimestamp);
  if (Number.isNaN(date.getTime())) return '';

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function formatOrderStatus(value: unknown) {
  if (typeof value === 'number') return ORDER_STATUS_LABELS[value] ?? String(value);

  const status = toStringValue(value);
  if (!status) return '未知';
  const numericStatus = Number(status);

  if (Number.isFinite(numericStatus) && status.trim() !== '') {
    return ORDER_STATUS_LABELS[numericStatus] ?? status;
  }

  return status;
}

function toStringValue(value: unknown) {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return '';
}

function pad(value: number) {
  return String(value).padStart(2, '0');
}

function removeEmptyFields(payload: Record<string, unknown>) {
  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined && value !== '')
  );
}

const ORDER_STATUS_LABELS: Record<number, string> = {
  0: '待付款',
  1: '已付款',
  2: '已取消',
  3: '已退款',
  4: '已关闭'
};
